"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiCheck, FiEdit2, FiPlus } from "react-icons/fi";
import { Button } from "@/components/ui";
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from "@/components/ui/dropdown";
import Input from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SearchSelect, SearchSelectContent, SearchSelectItem, SearchSelectTrigger, SearchSelectValue } from "@/components/ui/select";
import { formatDilutionLabel, formatDilutionStrength } from "@/lib/dilution";
import { formatToHumanReadable, formatUnit } from "@/lib/format-data";
import { canonicalMedicationsByName } from "@/lib/medication-select";
import { cx } from "@/lib/tailwind-utils";
import { Unit } from "@/types/enums/unit";
import type { Medication, MedicationDilution } from "@/types/medication";
import { medicationsQueryOptions } from "@/query/medications/medications";

/** The clickable face of an editable cell: reads as plain text until hovered. */
const CellButton = React.forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<"button"> & {
  isEmpty?: boolean;
  placeholder?: string;
}>((t0, ref) => {
  const $ = _c(20);
  let children;
  let className;
  let isEmpty;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      isEmpty,
      placeholder: t1,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = isEmpty;
    $[4] = props;
    $[5] = t1;
  } else {
    children = $[1];
    className = $[2];
    isEmpty = $[3];
    props = $[4];
    t1 = $[5];
  }
  const placeholder = t1 === undefined ? "Add" : t1;
  let t2;
  if ($[6] !== className) {
    t2 = cx("group/cell flex min-h-8 w-full min-w-0 items-center justify-between gap-1 rounded-lg px-2 py-1 text-left text-body outline-none transition-colors", "hover:bg-hover focus-visible:bg-hover focus-visible:ring-2 focus-visible:ring-primary/40", "data-[state=open]:bg-primary/10 data-[state=open]:ring-2 data-[state=open]:ring-primary/40", className);
    $[6] = className;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== isEmpty || $[10] !== placeholder) {
    t3 = isEmpty ? <span className="text-foreground-muted">{placeholder}</span> : children;
    $[8] = children;
    $[9] = isEmpty;
    $[10] = placeholder;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t3) {
    t4 = <span className="min-w-0 flex-1 whitespace-normal break-words">{t3}</span>;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <FiEdit2 aria-hidden={true} className="size-3 shrink-0 text-foreground-muted opacity-0 transition-opacity group-hover/cell:opacity-100 group-focus-visible/cell:opacity-100" />;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== props || $[16] !== ref || $[17] !== t2 || $[18] !== t4) {
    t6 = <button ref={ref} type="button" className={t2} {...props}>{t4}{t5}</button>;
    $[15] = props;
    $[16] = ref;
    $[17] = t2;
    $[18] = t4;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
});
CellButton.displayName = "CellButton";

/** A cell's value when the table is read-only. */
export function ReadOnlyCell(t0) {
  const $ = _c(5);
  const {
    children,
    isEmpty
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== isEmpty) {
    t1 = isEmpty ? <span className="text-foreground-muted">—</span> : children;
    $[0] = children;
    $[1] = isEmpty;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="min-h-8 whitespace-normal break-words px-2 py-1 text-body">{t1}</div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
interface TextSuggestCellProps {
  label: string;
  value: string;
  suggestions: string[];
  placeholder?: string;
  inputPlaceholder?: string;
  onSave: (value: string) => void;
}

/** Free text with the values already used offered underneath. Enter saves what is typed. */
export function TextSuggestCell(t0) {
  const $ = _c(48);
  const {
    label,
    value,
    suggestions,
    placeholder,
    inputPlaceholder,
    onSave
  } = t0;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [highlight, setHighlight] = useState(-1);
  let t1;
  let t2;
  if ($[0] !== open || $[1] !== value) {
    t1 = () => {
      if (open) {
        setDraft(value);
        setHighlight(-1);
      }
    };
    t2 = [open, value];
    $[0] = open;
    $[1] = value;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== draft || $[5] !== suggestions) {
    const query = draft.trim().toLowerCase();
    t3 = suggestions.filter(option => option.toLowerCase() !== query && (!query || option.toLowerCase().includes(query))).slice(0, 8);
    $[4] = draft;
    $[5] = suggestions;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const matches = t3;
  let t4;
  if ($[7] !== onSave || $[8] !== value) {
    t4 = next => {
      setOpen(false);
      if (next.trim() !== value.trim()) {
        onSave(next.trim());
      }
    };
    $[7] = onSave;
    $[8] = value;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const commit = t4;
  const t5 = !value;
  const t6 = `Edit ${label}`;
  let t7;
  if ($[10] !== placeholder || $[11] !== t5 || $[12] !== t6 || $[13] !== value) {
    t7 = <PopoverTrigger asChild={true}><CellButton isEmpty={t5} placeholder={placeholder} aria-label={t6}>{value}</CellButton></PopoverTrigger>;
    $[10] = placeholder;
    $[11] = t5;
    $[12] = t6;
    $[13] = value;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = event => {
      setDraft(event.target.value);
      setHighlight(-1);
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== commit || $[17] !== draft || $[18] !== highlight || $[19] !== matches) {
    t9 = event_0 => {
      if (event_0.key === "ArrowDown") {
        event_0.preventDefault();
        setHighlight(index => Math.min(matches.length - 1, index + 1));
      } else {
        if (event_0.key === "ArrowUp") {
          event_0.preventDefault();
          setHighlight(_temp);
        } else {
          if (event_0.key === "Enter") {
            event_0.preventDefault();
            commit(highlight >= 0 ? matches[highlight] : draft);
          }
        }
      }
    };
    $[16] = commit;
    $[17] = draft;
    $[18] = highlight;
    $[19] = matches;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== draft || $[22] !== inputPlaceholder || $[23] !== label || $[24] !== t9) {
    t10 = <Input autoFocus={true} aria-label={label} value={draft} placeholder={inputPlaceholder} onChange={t8} onKeyDown={t9} />;
    $[21] = draft;
    $[22] = inputPlaceholder;
    $[23] = label;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== commit || $[27] !== highlight || $[28] !== matches) {
    t11 = matches.length > 0 && <div className="mt-2 max-h-56 overflow-y-auto" role="listbox"><p className="px-2 pb-1 text-caption text-foreground-muted">Used before</p>{matches.map((option_0, index_1) => <button key={option_0} type="button" role="option" aria-selected={index_1 === highlight} className={cx("flex w-full items-center rounded-lg px-2 py-1.5 text-left text-body hover:bg-hover", index_1 === highlight && "bg-hover")} onMouseEnter={() => setHighlight(index_1)} onClick={() => commit(option_0)}>{option_0}</button>)}</div>;
    $[26] = commit;
    $[27] = highlight;
    $[28] = matches;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span className="text-caption text-foreground-muted">Enter to save · Esc to cancel</span>;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== commit || $[32] !== value) {
    t13 = value && <Button variant="ghost" size="sm" onClick={() => commit("")}>Clear</Button>;
    $[31] = commit;
    $[32] = value;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== commit || $[35] !== draft) {
    t14 = <Button variant="primary" size="sm" onClick={() => commit(draft)}>Save</Button>;
    $[34] = commit;
    $[35] = draft;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t13 || $[38] !== t14) {
    t15 = <div className="mt-2 flex items-center justify-between gap-2 border-t border-border pt-2">{t12}<div className="flex gap-1">{t13}{t14}</div></div>;
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t10 || $[41] !== t11 || $[42] !== t15) {
    t16 = <PopoverContent align="start" sideOffset={4} className="w-72 p-2">{t10}{t11}{t15}</PopoverContent>;
    $[40] = t10;
    $[41] = t11;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== open || $[45] !== t16 || $[46] !== t7) {
    t17 = <Popover open={open} onOpenChange={setOpen}>{t7}{t16}</Popover>;
    $[44] = open;
    $[45] = t16;
    $[46] = t7;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  return t17;
}
function _temp(index_0) {
  return Math.max(-1, index_0 - 1);
}
interface DilutionCellProps {
  dilutions: MedicationDilution[];
  value: string | null | undefined;
  unit?: string | null;
  ariaLabel?: string;
  onSave: (dilutionId: string) => void;
}

/** Pick from the medication's set-up dilutions; never free text. */
export function DilutionCell(t0) {
  const $ = _c(40);
  const {
    dilutions,
    value,
    unit,
    ariaLabel: t1,
    onSave
  } = t0;
  const ariaLabel = t1 === undefined ? "Edit reconstitution" : t1;
  let T0;
  let T1;
  let T2;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== ariaLabel || $[1] !== dilutions || $[2] !== value) {
    let t7;
    if ($[11] !== value) {
      t7 = dilution => dilution.id === value;
      $[11] = value;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    const current = dilutions.find(t7);
    T2 = DropdownMenu;
    T1 = DropdownMenuTrigger;
    t6 = true;
    T0 = CellButton;
    t2 = !current;
    t3 = "Set reconstitution";
    t4 = ariaLabel;
    t5 = current && <span className="tabular-nums">{formatDilutionLabel(current.diluentVolume)}</span>;
    $[0] = ariaLabel;
    $[1] = dilutions;
    $[2] = value;
    $[3] = T0;
    $[4] = T1;
    $[5] = T2;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    T0 = $[3];
    T1 = $[4];
    T2 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[13] !== T0 || $[14] !== t2 || $[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
    t7 = <T0 isEmpty={t2} placeholder={t3} aria-label={t4}>{t5}</T0>;
    $[13] = T0;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== T1 || $[20] !== t6 || $[21] !== t7) {
    t8 = <T1 asChild={t6}>{t7}</T1>;
    $[19] = T1;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DropdownMenuLabel>Reconstitution</DropdownMenuLabel>;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const t10 = value ?? "";
  let t11;
  if ($[24] !== onSave || $[25] !== value) {
    t11 = next => next !== value && onSave(next);
    $[24] = onSave;
    $[25] = value;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== dilutions || $[28] !== unit) {
    let t13;
    if ($[30] !== unit) {
      t13 = dilution_0 => {
        const strength = formatDilutionStrength(dilution_0, unit);
        return <DropdownMenuRadioItem key={dilution_0.id} value={dilution_0.id ?? ""}><span className="tabular-nums">{formatDilutionLabel(dilution_0.diluentVolume)}</span>{strength && <span className="ml-2 text-caption text-foreground-muted tabular-nums">{strength}</span>}</DropdownMenuRadioItem>;
      };
      $[30] = unit;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    t12 = dilutions.map(t13);
    $[27] = dilutions;
    $[28] = unit;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t12) {
    t13 = <DropdownMenuContent align="start">{t9}<DropdownMenuRadioGroup value={t10} onValueChange={t11}>{t12}</DropdownMenuRadioGroup></DropdownMenuContent>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== T2 || $[37] !== t13 || $[38] !== t8) {
    t14 = <T2>{t8}{t13}</T2>;
    $[36] = T2;
    $[37] = t13;
    $[38] = t8;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  return t14;
}
interface SiteCellProps {
  value: string[];
  options: string[];
  /** Shown in place of the site chips, e.g. when rows below list them. */
  summary?: string;
  onSave: (labels: string[]) => void;
}

/** Pick any number of sites; a site not on the list can be added by typing it. */
export function SiteCell({
  value,
  options,
  summary,
  onSave
}: SiteCellProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(value);
  const [search, setSearch] = useState("");
  const [added, setAdded] = useState<string[]>([]);
  const valueRef = useRef(value);
  valueRef.current = value;
  useEffect(() => {
    if (open) {
      setSelected(valueRef.current);
      setSearch("");
      setAdded([]);
    }
  }, [open]);
  const allOptions = useMemo(() => {
    const seen = new Set<string>();
    return [...value, ...added, ...options].filter(option => {
      const key = option.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [added, options, value]);
  const query = search.trim();
  const visible = allOptions.filter(option_0 => !query || option_0.toLowerCase().includes(query.toLowerCase()));
  const canAdd = !!query && !allOptions.some(option_1 => option_1.toLowerCase() === query.toLowerCase());
  const isSelected = (option_2: string) => selected.some(item => item.toLowerCase() === option_2.toLowerCase());
  const toggle = (option_3: string) => setSelected(current => isSelected(option_3) ? current.filter(item_0 => item_0.toLowerCase() !== option_3.toLowerCase()) : [...current, option_3]);
  const addTyped = () => {
    if (!canAdd) return;
    setAdded(current_0 => [query, ...current_0]);
    setSelected(current_1 => [...current_1, query]);
    setSearch("");
  };

  // Esc throws the picks away, like the text cells; Done or a click outside saves them.
  const cancelledRef = useRef(false);
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) return;
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    const changed = selected.length !== value.length || selected.some((item_1, index) => item_1 !== value[index]);
    if (changed) onSave(selected);
  };
  return <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <CellButton isEmpty={value.length === 0} placeholder="Add site" aria-label="Edit site">
          {summary ? <span className="text-foreground-muted">{summary}</span> : <span className="flex flex-wrap gap-1">
              {value.map(label => <span key={label} className="rounded-md bg-muted px-1.5 py-0.5 text-caption-strong text-foreground">
                  {label}
                </span>)}
            </span>}
        </CellButton>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={4} className="w-72 p-2" onEscapeKeyDown={() => {
      cancelledRef.current = true;
    }}>
        <Input autoFocus aria-label="Search sites" placeholder="Search or add a site..." value={search} onChange={event => setSearch(event.target.value)} onKeyDown={event_0 => {
        if (event_0.key !== "Enter") return;
        event_0.preventDefault();
        if (canAdd) addTyped();else if (visible.length === 1) toggle(visible[0]);
      }} />
        <div className="mt-2 max-h-64 overflow-y-auto" role="listbox">
          {canAdd && <button type="button" className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-body text-primary hover:bg-hover" onClick={addTyped}>
              <FiPlus className="size-3.5" />
              Add &ldquo;{query}&rdquo;
            </button>}
          {visible.map(option_4 => {
          const checked = isSelected(option_4);
          return <button key={option_4} type="button" role="option" aria-selected={checked} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-body hover:bg-hover" onClick={() => toggle(option_4)}>
                <span className={cx("flex size-4 shrink-0 items-center justify-center rounded border", checked ? "border-primary bg-primary text-primary-foreground" : "border-border")}>
                  {checked && <FiCheck className="size-3" />}
                </span>
                {option_4}
              </button>;
        })}
          {visible.length === 0 && !canAdd && <p className="px-2 py-1.5 text-caption text-foreground-muted">
              No sites match.
            </p>}
        </div>
        <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
          <span className="text-caption text-foreground-muted">
            {`${selected.length} selected · Esc to cancel`}
          </span>
          <Button variant="secondary" size="sm" onClick={() => handleOpenChange(false)}>
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>;
}
interface MedicationCellProps {
  value: string;
  onSave: (medication: Medication) => void;
}

/** One medication from the catalog, picked by name like the dose form. */
export function MedicationCell(t0) {
  const $ = _c(19);
  const {
    value,
    onSave
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = medicationsQueryOptions.list();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: t2,
    isLoading
  } = useQuery(t1);
  let t3;
  if ($[1] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const medications = t3;
  let t4;
  if ($[3] !== medications) {
    t4 = canonicalMedicationsByName(medications);
    $[3] = medications;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const canonicalByName = t4;
  let t5;
  if ($[5] !== canonicalByName || $[6] !== onSave || $[7] !== value) {
    t5 = name => {
      const medication = canonicalByName.get(name);
      if (medication && name !== value) {
        onSave(medication);
      }
    };
    $[5] = canonicalByName;
    $[6] = onSave;
    $[7] = value;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SearchSelectTrigger wrap={true} aria-label="Edit medication" className="min-h-8 rounded-lg border-transparent px-2 py-1 font-medium shadow-none hover:bg-hover data-[state=open]:bg-primary/10"><SearchSelectValue placeholder="Pick medication" formatValue={formatToHumanReadable} /></SearchSelectTrigger>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = isLoading ? "Loading..." : "No medications found.";
  let t8;
  if ($[10] !== canonicalByName) {
    t8 = Array.from(canonicalByName.entries()).map(_temp2);
    $[10] = canonicalByName;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = <SearchSelectContent searchPlaceholder="Search medications..." emptyMessage={t7} className="z-[900] w-72">{t8}</SearchSelectContent>;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t5 || $[16] !== t9 || $[17] !== value) {
    t10 = <SearchSelect value={value} onValueChange={t5}>{t6}{t9}</SearchSelect>;
    $[15] = t5;
    $[16] = t9;
    $[17] = value;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
}
function _temp2(t0) {
  const [name_0, medication_0] = t0;
  return <SearchSelectItem key={name_0} value={name_0}>{`${formatToHumanReadable(name_0)}${medication_0.unit ? ` (${formatUnit(medication_0.unit as Unit)})` : ""}`}</SearchSelectItem>;
}
interface AmountCellProps {
  amount?: number;
  unit?: Unit | null;
  quantity?: number;
  ariaLabel?: string;
  onSave: (amount: number) => void;
}
export function AmountCell(t0) {
  const $ = _c(49);
  const {
    amount,
    unit,
    quantity,
    ariaLabel: t1,
    onSave
  } = t0;
  const ariaLabel = t1 === undefined ? "Edit amount" : t1;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  let t2;
  if ($[0] !== unit) {
    t2 = unit ? formatUnit(unit) : "";
    $[0] = unit;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const unitLabel = t2;
  let t3;
  let t4;
  if ($[2] !== amount || $[3] !== open) {
    t3 = () => {
      if (open) {
        setDraft(amount != null ? String(amount) : "");
      }
    };
    t4 = [amount, open];
    $[2] = amount;
    $[3] = open;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  const parsed = Number(draft);
  let t5;
  if ($[6] !== draft || $[7] !== parsed) {
    t5 = draft.trim() !== "" && Number.isFinite(parsed) && parsed > 0;
    $[6] = draft;
    $[7] = parsed;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const isValid = t5;
  let t6;
  if ($[9] !== amount || $[10] !== isValid || $[11] !== onSave || $[12] !== parsed) {
    t6 = () => {
      if (!isValid) {
        return;
      }
      setOpen(false);
      if (parsed !== amount) {
        onSave(parsed);
      }
    };
    $[9] = amount;
    $[10] = isValid;
    $[11] = onSave;
    $[12] = parsed;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const commit = t6;
  const t7 = amount == null;
  let t8;
  if ($[14] !== amount || $[15] !== unitLabel) {
    t8 = <span className="whitespace-nowrap tabular-nums">{amount} {unitLabel}</span>;
    $[14] = amount;
    $[15] = unitLabel;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== quantity) {
    t9 = quantity != null && quantity !== 1 && <span className="ml-1 text-caption text-foreground-muted">×{quantity}</span>;
    $[17] = quantity;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== ariaLabel || $[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t10 = <PopoverTrigger asChild={true}><CellButton isEmpty={t7} placeholder="Add amount" aria-label={ariaLabel}>{t8}{t9}</CellButton></PopoverTrigger>;
    $[19] = ariaLabel;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== draft || $[25] !== isValid) {
    t11 = draft.trim() !== "" && !isValid;
    $[24] = draft;
    $[25] = isValid;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = event => setDraft(event.target.value);
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== commit) {
    t13 = event_0 => {
      if (event_0.key === "Enter") {
        event_0.preventDefault();
        commit();
      }
    };
    $[28] = commit;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== draft || $[31] !== t11 || $[32] !== t13) {
    t14 = <Input autoFocus={true} type="number" inputMode="decimal" min={0} step="any" aria-label="Amount" value={draft} hasError={t11} onChange={t12} onKeyDown={t13} />;
    $[30] = draft;
    $[31] = t11;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== unitLabel) {
    t15 = unitLabel && <span className="shrink-0 text-body text-foreground-secondary">{unitLabel}</span>;
    $[34] = unitLabel;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t14 || $[37] !== t15) {
    t16 = <div className="flex items-center gap-2">{t14}{t15}</div>;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = !isValid;
  let t18;
  if ($[39] !== commit || $[40] !== t17) {
    t18 = <div className="mt-2 flex justify-end"><Button variant="primary" size="sm" disabled={t17} onClick={commit}>Save</Button></div>;
    $[39] = commit;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t16 || $[43] !== t18) {
    t19 = <PopoverContent align="start" sideOffset={4} className="w-56 p-2">{t16}{t18}</PopoverContent>;
    $[42] = t16;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== open || $[46] !== t10 || $[47] !== t19) {
    t20 = <Popover open={open} onOpenChange={setOpen}>{t10}{t19}</Popover>;
    $[45] = open;
    $[46] = t10;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  return t20;
}